import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { registrationsAPI } from './api';
import './Admin.css';

const Registrations = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  
  // Filters
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    search: searchParams.get('search') || '',
  });
  
  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await registrationsAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status,
        search: filters.search,
      });
      
      if (response.success) {
        setRegistrations(response.data?.registrations || []);
        setPagination(prev => ({
          ...prev,
          total: response.data?.pagination?.total || 0,
          totalPages: response.data?.pagination?.totalPages || 0,
        }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters.status, filters.search]);

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
    
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    
    try {
      setDeleting(true);
      await registrationsAPI.delete(deleteId);
      setShowDeleteModal(false);
      setDeleteId(null);
      fetchRegistrations();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await registrationsAPI.update(id, { status: newStatus });
      fetchRegistrations();
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { class: 'bg-warning text-dark', text: 'Mới' },
      pending: { class: 'bg-warning text-dark', text: 'Chờ xử lý' },
      contacted: { class: 'bg-info', text: 'Đã liên hệ' },
      in_progress: { class: 'bg-primary', text: 'Đang xử lý' },
      completed: { class: 'bg-success', text: 'Hoàn thành' },
      cancelled: { class: 'bg-danger', text: 'Đã hủy' },
    };
    const badge = badges[status] || { class: 'bg-secondary', text: status };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <nav aria-label="Pagination" className="pagination-wrapper">
        <ul className="pagination justify-content-center">
          <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
            <button 
              className="page-link" 
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
          </li>
          <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
            <button 
              className="page-link" 
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              <i className="fas fa-angle-left"></i>
            </button>
          </li>
          
          {pages.map(page => (
            <li key={page} className={`page-item ${pagination.page === page ? 'active' : ''}`}>
              <button 
                className="page-link" 
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            </li>
          ))}
          
          <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
            <button 
              className="page-link" 
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
            >
              <i className="fas fa-angle-right"></i>
            </button>
          </li>
          <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
            <button 
              className="page-link" 
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </li>
        </ul>
        <div className="pagination-info">
          Trang {pagination.page} / {pagination.totalPages} (Tổng: {pagination.total} đăng ký)
        </div>
      </nav>
    );
  };

  return (
    <div className="admin-registrations">
      <div className="page-header">
        <h1>
          <i className="fas fa-list-alt me-3"></i>
          Quản lý đăng ký
        </h1>
        <p>Danh sách tất cả các đăng ký tư vấn điện mặt trời</p>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="row g-3">
          <div className="col-md-4">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Tìm theo tên hoặc SĐT..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
              {filters.search && (
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => handleFilterChange('search', '')}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
          
          <div className="col-md-3">
            <select
              className="form-select"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">Tất cả trạng thái</option>
              <option value="new">Mới</option>
              <option value="contacted">Đã liên hệ</option>
              <option value="in_progress">Đang xử lý</option>
              <option value="completed">Hoàn thành</option>
              <option value="cancelled">Đã hủy</option>
            </select>
          </div>
          
          <div className="col-md-2">
            <select
              className="form-select"
              value={pagination.limit}
              onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
            >
              <option value="10">10 / trang</option>
              <option value="25">25 / trang</option>
              <option value="50">50 / trang</option>
              <option value="100">100 / trang</option>
            </select>
          </div>
          
          <div className="col-md-3 text-end">
            <button className="btn btn-outline-primary" onClick={fetchRegistrations}>
              <i className="fas fa-sync-alt me-2"></i>
              Làm mới
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="admin-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Đang tải dữ liệu...</p>
        </div>
      ) : registrations.length === 0 ? (
        <div className="no-data">
          <i className="fas fa-inbox"></i>
          <h3>Không có dữ liệu</h3>
          <p>
            {filters.search || filters.status 
              ? 'Không tìm thấy đăng ký nào phù hợp với bộ lọc' 
              : 'Chưa có đăng ký nào trong hệ thống'}
          </p>
          {(filters.search || filters.status) && (
            <button 
              className="btn btn-outline-primary"
              onClick={() => {
                setFilters({ status: '', search: '' });
                setSearchParams(new URLSearchParams());
              }}
            >
              <i className="fas fa-times me-2"></i>
              Xóa bộ lọc
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="table-responsive">
            <table className="table table-hover registrations-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>#</th>
                  <th>Họ tên</th>
                  <th>Số điện thoại</th>
                  <th>Tỉnh/TP</th>
                  <th>Loại điện</th>
                  <th>Tiền điện/tháng</th>
                  <th>Trạng thái</th>
                  <th>Ngày đăng ký</th>
                  <th style={{ width: '180px' }}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((reg, index) => (
                  <tr key={reg.id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>
                      <strong>{reg.full_name}</strong>
                    </td>
                    <td>
                      <a href={`tel:${reg.phone}`} className="phone-link">
                        <i className="fas fa-phone me-1"></i>
                        {reg.phone}
                      </a>
                    </td>
                    <td>{reg.province}</td>
                    <td>
                      <span className={`badge ${reg.electric_type === 'business' ? 'bg-primary' : 'bg-secondary'}`}>
                        {reg.electric_type === 'business' ? 'Kinh doanh' : 'Sinh hoạt'}
                      </span>
                    </td>
                    <td>{reg.monthly_bill ? `${parseInt(reg.monthly_bill).toLocaleString('vi-VN')} đ` : '-'}</td>
                    <td>
                      <div className="dropdown">
                        <button 
                          className="btn btn-sm dropdown-toggle p-0 border-0 bg-transparent"
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          {getStatusBadge(reg.status)}
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <button 
                              className="dropdown-item"
                              onClick={() => handleStatusUpdate(reg.id, 'new')}
                            >
                              <span className="badge bg-warning text-dark me-2">●</span>
                              Mới
                            </button>
                          </li>
                          <li>
                            <button 
                              className="dropdown-item"
                              onClick={() => handleStatusUpdate(reg.id, 'contacted')}
                            >
                              <span className="badge bg-info me-2">●</span>
                              Đã liên hệ
                            </button>
                          </li>
                          <li>
                            <button 
                              className="dropdown-item"
                              onClick={() => handleStatusUpdate(reg.id, 'in_progress')}
                            >
                              <span className="badge bg-primary me-2">●</span>
                              Đang xử lý
                            </button>
                          </li>
                          <li>
                            <button 
                              className="dropdown-item"
                              onClick={() => handleStatusUpdate(reg.id, 'completed')}
                            >
                              <span className="badge bg-success me-2">●</span>
                              Hoàn thành
                            </button>
                          </li>
                          <li><hr className="dropdown-divider" /></li>
                          <li>
                            <button 
                              className="dropdown-item text-danger"
                              onClick={() => handleStatusUpdate(reg.id, 'cancelled')}
                            >
                              <span className="badge bg-danger me-2">●</span>
                              Đã hủy
                            </button>
                          </li>
                        </ul>
                      </div>
                    </td>
                    <td>{formatDate(reg.created_at)}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <Link 
                          to={`/admin/registrations/${reg.id}`} 
                          className="btn btn-outline-primary"
                          title="Xem chi tiết"
                        >
                          <i className="fas fa-eye"></i>
                        </Link>
                        <a 
                          href={`tel:${reg.phone}`} 
                          className="btn btn-outline-success"
                          title="Gọi điện"
                        >
                          <i className="fas fa-phone"></i>
                        </a>
                        <button 
                          className="btn btn-outline-danger"
                          onClick={() => handleDeleteClick(reg.id)}
                          title="Xóa"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {renderPagination()}
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-backdrop-custom">
          <div className="modal-dialog-custom">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-danger"></i>
                  Xác nhận xóa
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  aria-label="Close"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <p><strong>Bạn có chắc chắn muốn xóa đăng ký này?</strong></p>
                <p className="text-muted">Hành động này không thể hoàn tác.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  <i className="fas fa-times me-2"></i>
                  Hủy
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Đang xóa...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Xóa
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Registrations;